import type { MyChronGpsPoint } from './telemetrySession';
import type { GoProExtractSummary } from './goproTelemetry';

const DB_NAME = 'paddiq-gopro-upload-state';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';
const PREVIEW_STORE_NAME = 'previews';
const MANIFEST_KEY = 'paddiq-gopro-upload-manifest-v1';

export type GoProUploadObjectKey = 'video' | 'telemetrySummary' | 'telemetry';

export type GoProUploadPersistedSession = {
  uploadSessionId?: string;
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  uploadMode?: 'gopro' | 'video';
  uploadedBytes: Partial<Record<GoProUploadObjectKey, number>>;
  phase:
    | 'staging'
    | 'extracting'
    | 'matching'
    | 'uploading-json'
    | 'uploading-video'
    | 'paused'
    | 'done';
  jobId?: string;
  updatedAt: number;
};

export type GoProExtractPreviewCache = {
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  summary: GoProExtractSummary;
  gps: MyChronGpsPoint[];
  telemetry?: unknown;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(PREVIEW_STORE_NAME)) {
        db.createObjectStore(PREVIEW_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
  });
}

async function idbGet(key: string): Promise<GoProUploadPersistedSession | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(key);
    request.onsuccess = () =>
      resolve((request.result as GoProUploadPersistedSession | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
  });
}

async function idbSet(key: string, value: GoProUploadPersistedSession) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const request = tx.objectStore(STORE_NAME).put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed'));
  });
}

async function idbDelete(key: string) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const request = tx.objectStore(STORE_NAME).delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB delete failed'));
  });
}

function readManifest(): string[] {
  const raw = localStorage.getItem(MANIFEST_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeManifest(keys: string[]) {
  localStorage.setItem(MANIFEST_KEY, JSON.stringify(keys));
}

export function buildGoProFileFingerprint(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}

export async function saveGoProUploadSession(session: GoProUploadPersistedSession) {
  const key = buildGoProFileFingerprint({
    name: session.fileName,
    size: session.fileSize,
    lastModified: session.fileLastModified,
  });
  await idbSet(key, { ...session, updatedAt: Date.now() });
  const manifest = readManifest().filter((entry) => entry !== key);
  manifest.unshift(key);
  writeManifest(manifest.slice(0, 5));
}

export async function loadGoProUploadSession(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  const key = buildGoProFileFingerprint(file);
  return idbGet(key);
}

export async function clearGoProUploadSession(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  const key = buildGoProFileFingerprint(file);
  await idbDelete(key);
  writeManifest(readManifest().filter((entry) => entry !== key));
}

export async function listPersistedGoProUploadSessions() {
  const manifest = readManifest();
  const sessions = await Promise.all(manifest.map((key) => idbGet(key)));
  return sessions.filter((session): session is GoProUploadPersistedSession => session != null);
}

export async function findPersistedGoProUploadSession(
  identity: Pick<File, 'name' | 'size' | 'lastModified'>,
) {
  const direct = await loadGoProUploadSession(identity);
  if (direct) {
    return direct;
  }

  const sessions = await listPersistedGoProUploadSessions();
  return (
    sessions.find(
      (session) =>
        session.fileSize === identity.size && session.fileLastModified === identity.lastModified,
    ) ?? null
  );
}

export async function repairGoProUploadSessionIdentity(session: GoProUploadPersistedSession) {
  const correctedName = inferOriginalGoProFileName(session);
  const corrected: GoProUploadPersistedSession = {
    ...session,
    fileName: correctedName,
  };

  const oldKey = buildGoProFileFingerprint({
    name: session.fileName,
    size: session.fileSize,
    lastModified: session.fileLastModified,
  });
  const newKey = buildGoProFileFingerprint(sessionFileIdentity(corrected));

  if (oldKey !== newKey) {
    await idbDelete(oldKey);
    writeManifest(readManifest().filter((entry) => entry !== oldKey));
  }

  await saveGoProUploadSession(corrected);
  return corrected;
}

export function inferOriginalGoProFileName(session: GoProUploadPersistedSession) {
  const suffix = `-${String(session.fileSize)}-${String(session.fileLastModified)}.mp4`;
  if (session.fileName.endsWith(suffix) && session.fileName.length > suffix.length) {
    return session.fileName.slice(0, -suffix.length);
  }

  return session.fileName;
}

export function fileMatchesPersistedSession(file: File, session: GoProUploadPersistedSession) {
  const originalName = inferOriginalGoProFileName(session);
  return (
    file.size === session.fileSize &&
    file.lastModified === session.fileLastModified &&
    (file.name === session.fileName || file.name === originalName)
  );
}

export function sessionFileIdentity(session: GoProUploadPersistedSession) {
  return {
    name: inferOriginalGoProFileName(session),
    size: session.fileSize,
    lastModified: session.fileLastModified,
  };
}

export async function loadGoProExtractPreviewForSession(session: GoProUploadPersistedSession) {
  return loadGoProExtractPreview(sessionFileIdentity(session));
}

export async function saveGoProExtractPreview(
  file: File,
  preview: Omit<GoProExtractPreviewCache, 'fileName' | 'fileSize' | 'fileLastModified'>,
) {
  const key = buildGoProFileFingerprint(file);
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PREVIEW_STORE_NAME, 'readwrite');
    const request = tx.objectStore(PREVIEW_STORE_NAME).put(
      {
        fileName: file.name,
        fileSize: file.size,
        fileLastModified: file.lastModified,
        ...preview,
      } satisfies GoProExtractPreviewCache,
      key,
    );
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed'));
  });
}

export async function loadGoProExtractPreviewWithFallback(
  identity: Pick<File, 'name' | 'size' | 'lastModified'>,
) {
  const direct = await loadGoProExtractPreview(identity);
  if (direct?.gps.length) {
    return direct;
  }

  const sessions = await listPersistedGoProUploadSessions();
  for (const session of sessions) {
    if (session.fileSize !== identity.size || session.fileLastModified !== identity.lastModified) {
      continue;
    }

    const fromSession = await loadGoProExtractPreviewForSession(session);
    if (fromSession?.gps.length) {
      return fromSession;
    }

    const legacy = await loadGoProExtractPreview({
      name: session.fileName,
      size: session.fileSize,
      lastModified: session.fileLastModified,
    });
    if (legacy?.gps.length) {
      return legacy;
    }
  }

  const db = await openDb();
  return new Promise<GoProExtractPreviewCache | null>((resolve, reject) => {
    const tx = db.transaction(PREVIEW_STORE_NAME, 'readonly');
    const request = tx.objectStore(PREVIEW_STORE_NAME).openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(null);
        return;
      }

      const preview = cursor.value as GoProExtractPreviewCache;
      if (
        preview.fileSize === identity.size &&
        preview.fileLastModified === identity.lastModified &&
        preview.gps.length > 0
      ) {
        resolve(preview);
        return;
      }

      cursor.continue();
    };
    request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
  });
}

export async function loadGoProExtractPreview(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  const key = buildGoProFileFingerprint(file);
  const db = await openDb();
  return new Promise<GoProExtractPreviewCache | null>((resolve, reject) => {
    const tx = db.transaction(PREVIEW_STORE_NAME, 'readonly');
    const request = tx.objectStore(PREVIEW_STORE_NAME).get(key);
    request.onsuccess = () =>
      resolve((request.result as GoProExtractPreviewCache | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
  });
}

export async function clearGoProExtractPreview(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  const key = buildGoProFileFingerprint(file);
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PREVIEW_STORE_NAME, 'readwrite');
    const request = tx.objectStore(PREVIEW_STORE_NAME).delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB delete failed'));
  });
}

export async function clearAllGoProUploadState() {
  localStorage.removeItem(MANIFEST_KEY);
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_NAME, PREVIEW_STORE_NAME], 'readwrite');
      const sessionRequest = tx.objectStore(STORE_NAME).clear();
      const previewRequest = tx.objectStore(PREVIEW_STORE_NAME).clear();
      sessionRequest.onerror = () =>
        reject(sessionRequest.error ?? new Error('IndexedDB upload-state clear failed'));
      previewRequest.onerror = () =>
        reject(previewRequest.error ?? new Error('IndexedDB preview clear failed'));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB clear failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB clear aborted'));
    });
  } finally {
    db.close();
  }
}
